import { FlattenedTask, GanttDependency, DependencyTypeMap } from '../types/gantt.types';

interface TaskSchedule {
  id: number;
  earlyStart: number;
  earlyFinish: number;
  lateStart: number;
  lateFinish: number;
  totalFloat: number;
  duration: number;
}

/**
 * Calculate critical path using CPM (Critical Path Method)
 * Returns set of task IDs that are on the critical path
 */
export function calculateCriticalPath(
  tasks: FlattenedTask[],
  dependencies: GanttDependency[]
): Set<number> {
  if (tasks.length === 0) {
    return new Set();
  }

  // Build task map and adjacency list
  const taskMap = new Map<number, FlattenedTask>();
  const schedule = new Map<number, TaskSchedule>();
  const predecessors = new Map<number, { taskId: number; lag: number; type: string }[]>();
  const successors = new Map<number, { taskId: number; lag: number; type: string }[]>();

  // Initialize
  tasks.forEach((task) => {
    taskMap.set(task.id, task);
    predecessors.set(task.id, []);
    successors.set(task.id, []);

    schedule.set(task.id, {
      id: task.id,
      earlyStart: 0,
      earlyFinish: 0,
      lateStart: Infinity,
      lateFinish: Infinity,
      totalFloat: 0,
      duration: task.duration || 1,
    });
  });

  // Build dependency graph
  dependencies.forEach((dep) => {
    if (!taskMap.has(dep.from) || !taskMap.has(dep.to)) {
      return; // Skip invalid dependencies
    }

    const type =
      typeof dep.type === 'number'
        ? DependencyTypeMap[dep.type] || 'FS'
        : dep.type || 'FS';

    predecessors.get(dep.to)?.push({
      taskId: dep.from,
      lag: dep.lagDays || 0,
      type,
    });

    successors.get(dep.from)?.push({
      taskId: dep.to,
      lag: dep.lagDays || 0,
      type,
    });
  });

  // Forward pass - calculate early start/finish
  const visited = new Set<number>();
  const forwardQueue: number[] = [];

  // Find tasks with no predecessors (start tasks)
  tasks.forEach((task) => {
    if (predecessors.get(task.id)?.length === 0) {
      forwardQueue.push(task.id);
    }
  });

  // If no start tasks found, use all tasks (may have circular deps)
  if (forwardQueue.length === 0 && tasks.length > 0) {
    forwardQueue.push(tasks[0].id);
  }

  let maxIterations = tasks.length * 2;
  while (forwardQueue.length > 0 && maxIterations > 0) {
    maxIterations--;
    const taskId = forwardQueue.shift()!;

    if (visited.has(taskId)) continue;

    const taskSched = schedule.get(taskId)!;
    const preds = predecessors.get(taskId) || [];

    // Calculate early start based on predecessors
    let earlyStart = 0;
    let allPredsVisited = true;

    for (const pred of preds) {
      if (!visited.has(pred.taskId)) {
        allPredsVisited = false;
        break;
      }

      const predSched = schedule.get(pred.taskId)!;
      let predDate: number;

      // Calculate based on dependency type
      switch (pred.type) {
        case 'FS':
          predDate = predSched.earlyFinish + pred.lag;
          break;
        case 'SS':
          predDate = predSched.earlyStart + pred.lag;
          break;
        case 'FF':
          predDate = predSched.earlyFinish + pred.lag - taskSched.duration;
          break;
        case 'SF':
          predDate = predSched.earlyStart + pred.lag - taskSched.duration;
          break;
        default:
          predDate = predSched.earlyFinish + pred.lag;
      }

      earlyStart = Math.max(earlyStart, predDate);
    }

    if (!allPredsVisited && preds.length > 0) {
      forwardQueue.push(taskId);
      continue;
    }

    taskSched.earlyStart = Math.max(0, earlyStart);
    taskSched.earlyFinish = taskSched.earlyStart + taskSched.duration;
    visited.add(taskId);

    // Add successors to queue
    successors.get(taskId)?.forEach((succ) => {
      if (!visited.has(succ.taskId) && !forwardQueue.includes(succ.taskId)) {
        forwardQueue.push(succ.taskId);
      }
    });
  }

  // Add any unvisited tasks
  tasks.forEach((task) => {
    if (!visited.has(task.id)) {
      visited.add(task.id);
      const taskSched = schedule.get(task.id)!;
      taskSched.earlyStart = 0;
      taskSched.earlyFinish = taskSched.duration;
    }
  });

  // Find project end date (max early finish)
  let projectEnd = 0;
  schedule.forEach((s) => {
    projectEnd = Math.max(projectEnd, s.earlyFinish);
  });

  // Backward pass - calculate late start/finish
  const backwardVisited = new Set<number>();
  const backwardQueue: number[] = [];

  // Find tasks with no successors (end tasks)
  tasks.forEach((task) => {
    if (successors.get(task.id)?.length === 0) {
      backwardQueue.push(task.id);
      const sched = schedule.get(task.id)!;
      sched.lateFinish = projectEnd;
      sched.lateStart = projectEnd - sched.duration;
    }
  });

  // If no end tasks, use all tasks
  if (backwardQueue.length === 0 && tasks.length > 0) {
    tasks.forEach((task) => {
      const sched = schedule.get(task.id)!;
      sched.lateFinish = projectEnd;
      sched.lateStart = projectEnd - sched.duration;
      backwardQueue.push(task.id);
    });
  }

  maxIterations = tasks.length * 2;
  while (backwardQueue.length > 0 && maxIterations > 0) {
    maxIterations--;
    const taskId = backwardQueue.shift()!;

    if (backwardVisited.has(taskId)) continue;

    const taskSched = schedule.get(taskId)!;
    const succs = successors.get(taskId) || [];

    // Calculate late finish based on successors
    let lateFinish = taskSched.lateFinish;
    let allSuccsVisited = true;

    for (const succ of succs) {
      if (!backwardVisited.has(succ.taskId) && successors.get(succ.taskId)?.length !== 0) {
        allSuccsVisited = false;
        break;
      }

      const succSched = schedule.get(succ.taskId)!;
      let succDate: number;

      switch (succ.type) {
        case 'FS':
          succDate = succSched.lateStart - succ.lag;
          break;
        case 'SS':
          succDate = succSched.lateStart - succ.lag + taskSched.duration;
          break;
        case 'FF':
          succDate = succSched.lateFinish - succ.lag;
          break;
        case 'SF':
          succDate = succSched.lateFinish - succ.lag + taskSched.duration;
          break;
        default:
          succDate = succSched.lateStart - succ.lag;
      }

      lateFinish = Math.min(lateFinish, succDate);
    }

    if (!allSuccsVisited && succs.length > 0) {
      backwardQueue.push(taskId);
      continue;
    }

    taskSched.lateFinish = lateFinish;
    taskSched.lateStart = lateFinish - taskSched.duration;
    taskSched.totalFloat = taskSched.lateStart - taskSched.earlyStart;

    backwardVisited.add(taskId);

    // Add predecessors to queue
    predecessors.get(taskId)?.forEach((pred) => {
      if (!backwardVisited.has(pred.taskId) && !backwardQueue.includes(pred.taskId)) {
        backwardQueue.push(pred.taskId);
      }
    });
  }

  // Handle unvisited tasks in backward pass
  tasks.forEach((task) => {
    if (!backwardVisited.has(task.id)) {
      const sched = schedule.get(task.id)!;
      sched.lateFinish = projectEnd;
      sched.lateStart = projectEnd - sched.duration;
      sched.totalFloat = sched.lateStart - sched.earlyStart;
    }
  });

  // Identify critical tasks (total float === 0 or very small)
  const criticalTasks = new Set<number>();
  schedule.forEach((sched, taskId) => {
    if (Math.abs(sched.totalFloat) < 0.5) {
      criticalTasks.add(taskId);
    }
  });

  return criticalTasks;
}

/**
 * Check if a dependency is on the critical path
 */
export function isCriticalDependency(
  dependency: GanttDependency,
  criticalTaskIds: Set<number>
): boolean {
  return criticalTaskIds.has(dependency.from) && criticalTaskIds.has(dependency.to);
}
